import { ChevronLeftTwoTone, HomeTwoTone } from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { addYears, format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { saveIndividualCitizenship, savePartyContactInfo } from '../api';
import { ContactFormValues } from '../components/ContactForm';
import { IndividualFormValues } from '../components/IndividualForm';
import { OnlineTourCard } from '../components/OnlineTourCard';
import { PurchaseSummary } from '../components/PurchaseSummary';
import { TourPurchaseContactInfo } from '../components/TourPurchaseContactInfo';
import { TourPurchaseGuestsInfoComponent } from '../components/TourPurchaseGuestsInfo';
import { TourPurchaseReservationOptions } from '../components/TourPurchaseReservationOptions';
import { companyId, contactMediumTypeId } from '../constants';
import { useTourPrices } from '../hooks/api/tour-prices';
import { useTourSearchDetail } from '../hooks/api/tour-search';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { useAuth } from '../hooks/useAuth';
import { useCart } from '../hooks/useCart';
import { usePersistedState } from '../hooks/usePersistedState';
import {
  calculatePrice,
  ChildInfo,
  getTourParam,
  groupPricesWithAgeGroups,
} from '../utils';
import { IOnlineTourPriceResult } from '../utils/types';

export type TourPurchaseNumberOfGuests = {
  adults: number;
  children: number;
  childrenInfo: ChildInfo[];
  priceId: number | null;
};

export type TourPurchaseGuestsInfo = {
  adults: (IndividualFormValues & {
    contact?: ContactFormValues;
    differentContactInfo?: boolean;
  })[];
  children: (IndividualFormValues & {
    contact?: ContactFormValues;
    differentContactInfo?: boolean;
    pricingId?: number;
  })[];
};

export default function TourPurchasePage() {
  const params = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const snackbar = useAlertSnackbar();
  const [guests, setGuests, resetGuests] =
    usePersistedState<TourPurchaseNumberOfGuests>(
      {
        adults: 2,
        children: 0,
        childrenInfo: [],
        priceId: null,
      },
      'tourPurchaseGuests',
    );
  const [guestsInfo, setGuestsInfo, resetGuestsInfo] =
    usePersistedState<TourPurchaseGuestsInfo>(
      {
        adults: [],
        children: [],
      },
      'tourPurchaseGuestsInfo',
    );
  const [sharedContactInfo, setSharedContactInfo, resetSharedContactInfo] =
    usePersistedState<ContactFormValues>(
      {
        email: {
          address: '',
        },
        phone: {
          countryCode: '',
          number: '',
        },
        address: {
          street1: '',
          doorNumber: '',
          geographicBoundaryId: undefined,
        },
      },
      'tourPurchaseSharedContactInfo',
    );
  const [contactErrors, setContactErrors] = useState<Record<string, string>>(
    {},
  );

  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const tourId = +(params.id as string);
  const { data, isLoading } = useTourSearchDetail({ id: tourId });
  const { data: prices, isLoading: pricesLoading } = useTourPrices({ tourId });
  const [sharedRoomingConsent, setSharedRoomingConsent] = useState(false);
  const { token } = useAuth();
  const { addToCart } = useCart();

  const adultPrices = groupPricesWithAgeGroups(prices || []);
  const allowSharedRooming = guests.adults === 1 && guests.children === 0;

  const selectedPrice = adultPrices.find((p) => p.price.id === guests.priceId);
  const selection = selectedPrice
    ? calculatePrice(
        guests,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        selectedPrice,
        { allowSharedRooming },
      )
    : null;

  const isSharedRoomingActive = (selection && selection.extraBeds > 0) || false;

  // check if reservation is allowed for the selected tour
  useEffect(() => {
    if (data) {
      if (
        getTourParam(data, 'RESERVATION_PERMISSION') !== '1' &&
        getTourParam(data, 'SALES_PERMISSION') !== '1'
      ) {
        // redirect to tour detail page
        nav(`/tours/${tourId}`);
        snackbar(t('tourPurchasePage.reservationNotAllowed'), 'error');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  async function handleNext() {
    if (activeStep === 0 && !token) {
      nav(`/login?next=${location.pathname}`);
      // scroll to top
      window.scrollTo(0, 0);
      return;
    }
    if (activeStep < 2) {
      setActiveStep(activeStep + 1);
      // scroll to top
      window.scrollTo(0, 0);
      return;
    }
    if (activeStep === 2) {
      handleAddToCart();
    }
  }

  function handlePrev() {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  }

  function resetState() {
    resetGuests();
    resetGuestsInfo();
    resetSharedContactInfo();
  }

  function filterOutIrrelevantGuestsInfo(g: TourPurchaseNumberOfGuests) {
    const newGuestsInfo: TourPurchaseGuestsInfo = {
      adults: guestsInfo.adults.slice(0, g.adults),
      children: guestsInfo.children.slice(0, g.children),
    };
    setGuestsInfo(newGuestsInfo);
  }

  async function handleIndividualSubmit(
    data: IndividualFormValues,
    type: 'adults' | 'children',
    position: number,
  ) {
    const res = await saveIndividualCitizenship(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        birthDate: format(data.birthDate, 'yyyy-MM-dd'),
        gender: data.gender,
        birthCountryId: data.birthCountry,
        citizenshipNumber: data.citizenshipNumber,
        countryId: data.citizenshipCountry,
        companyId,
      },
      token,
    );
    // create a new object to avoid mutation
    const newGuestsInfo = { ...guestsInfo };
    // ensure individual is unique
    if (
      ![-1, position].includes(
        newGuestsInfo[type].findIndex((g) => g.id === res.individual.id),
      )
    ) {
      throw new Error(t('tourPurchaseGuestsInfo.duplicateIndividualError'));
    }
    // check if an individual is saved at this position
    if (newGuestsInfo[type][position]) {
      // update object in position
      newGuestsInfo[type][position] = { ...data, id: res.individual.id };
    } else {
      // create new object
      newGuestsInfo[type].push({ ...data, id: res.individual.id });
    }
    setGuestsInfo(newGuestsInfo);
  }

  async function handleSharedContactInfoSubmit(data: ContactFormValues) {
    setSharedContactInfo(data);
  }

  async function handleGuestContactInfoSubmit(
    individualId: number,
    value: {
      differentContactInfo: boolean;
      contact?: ContactFormValues;
    },
  ) {
    if (!individualId) {
      throw new Error('Individual is not saved. Cannot save contact info.');
    }
    const newGuestsInfo = { ...guestsInfo };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    newGuestsInfo.adults = newGuestsInfo.adults.map(({ contact, ...g }) =>
      g.id === individualId
        ? {
            ...g,
            contact: value.contact,
            differentContactInfo: value.differentContactInfo,
          }
        : g,
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    newGuestsInfo.children = newGuestsInfo.children.map(({ contact, ...g }) =>
      g.id === individualId
        ? {
            ...g,
            contact: value.contact,
            differentContactInfo: value.differentContactInfo,
          }
        : g,
    );
    setGuestsInfo(newGuestsInfo);
  }

  async function saveContactInfo(g: TourPurchaseGuestsInfo['adults'][0]) {
    if (!g.id) {
      throw {
        message: 'Individual is not saved. Cannot save contact info.',
        individualId: g.id,
      };
    }
    let value = sharedContactInfo;
    if (g.differentContactInfo && g.contact) {
      value = g.contact;
    }
    if (!value || !value.address.geographicBoundaryId) return;
    const now = new Date();
    const fromDate = format(now, 'yyyy-MM-dd');
    const toDate = format(addYears(now, 100), 'yyyy-MM-dd');
    const usageTypeId = 9; // fixed

    try {
      const res = await savePartyContactInfo(token, g.id, {
        electronicAddress: {
          contactMediumInfo: {
            electronicAddress: value?.email.address || '',
            typeId: contactMediumTypeId.EMAIL,
          },
          usageTypeId,
          fromDate,
          toDate,
        },
        phoneNumber: {
          contactMediumInfo: {
            countryCode: value?.phone.countryCode || '',
            phoneNumber: value?.phone.number || '',
            typeId: contactMediumTypeId.PHONE,
          },
          fromDate,
          toDate,
          usageTypeId,
        },
        postalAddress: {
          contactMediumInfo: {
            geographicBoundaryId: value.address.geographicBoundaryId,
            typeId: contactMediumTypeId.ADDRESS,
            streetAddress1: value.address.street1,
            doorNumber: value.address.doorNumber,
          },
          fromDate,
          toDate,
          usageTypeId,
        },
      });

      return {
        email: {
          ...value.email,
          id: res.electronicAddress[0]?.contactMediumInfo.id,
        },
        phone: {
          ...value.phone,
          id: res.phoneNumber[0]?.contactMediumInfo.id,
        },
        address: {
          ...value.address,
          id: res.postalAddress[0]?.contactMediumInfo.id,
        },
      };
    } catch (e: any) {
      throw { individualId: g.id, message: e.message };
    }
  }

  async function handleAddToCart() {
    try {
      const contactsAdult = await Promise.all(
        guestsInfo.adults.map(saveContactInfo),
      );
      const contactsChildren = await Promise.all(
        guestsInfo.children.map(saveContactInfo),
      );

      try {
        if (!selection) throw new Error('Price is not selected');
        await addToCart(tourId, {
          ...guests,
          guests: {
            adults: guestsInfo.adults.map((g, i) => ({
              ...g,
              contact: contactsAdult[i],
            })),
            children: guestsInfo.children.map((g, i) => ({
              ...g,
              contact: contactsChildren[i],
              pricingId: selection.childrenPrices[i]?.id,
            })),
          },
        });
      } catch (e: any) {
        snackbar(e.message, 'error');
        return;
      }
      resetState();
      nav('/cart');
      // scroll to top
      window.scrollTo(0, 0);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      console.log(e);
      setContactErrors((prev) => ({
        ...prev,
        [e.individualId]: e.message,
      }));
    }
  }

  function hasContactInfoFilled(contact?: ContactFormValues) {
    return (
      contact &&
      contact.address.geographicBoundaryId &&
      contact.email.address &&
      contact.phone.countryCode &&
      contact.phone.number
    );
  }

  function checkStep() {
    switch (activeStep) {
      case 0:
        // check if consent given when shared rooming is active
        return (
          !isSharedRoomingActive ||
          (isSharedRoomingActive && sharedRoomingConsent)
        );
      case 1:
        // check if all adults and children have id
        return (
          guestsInfo.adults.filter((a) => a.id).length === guests.adults &&
          guestsInfo.children.filter((a) => a.id).length === guests.children
        );
      case 2:
        return guestsInfo.adults.every((g) =>
          g.differentContactInfo
            ? hasContactInfoFilled(g.contact)
            : hasContactInfoFilled(sharedContactInfo),
        );
      default:
        return false;
    }
  }

  return (
    <Box maxWidth={1440} marginX="auto" sx={{ pt: 2 }}>
      <Typography gutterBottom variant="h4" component="h1">
        {t('tourPurchasePage.title')}
      </Typography>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
      >
        <Link
          to="/"
          style={{ color: 'inherit', display: 'flex', alignItems: 'center' }}
        >
          <HomeTwoTone />
        </Link>
        <Link to={`/tours`} style={{ color: 'inherit' }}>
          {t('tourPurchasePage.tours')}
        </Link>
        <Box>
          <Link
            to={`/tours/${tourId}`}
            style={{
              maxWidth: 130,
              color: 'inherit',
              display: 'block',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {data?.name}
          </Link>
        </Box>
        <Typography>{t('tourPurchasePage.title')}</Typography>
      </Breadcrumbs>
      <Grid2 container rowSpacing={3} columnSpacing={[0, 0, 3]}>
        <Grid2 md={8}>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>{t('tourPurchasePage.reservationOptions')}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{t('tourPurchasePage.guestInformation')}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{t('tourPurchasePage.contactInformation')}</StepLabel>
            </Step>
          </Stepper>
          {activeStep === 0 && (
            <TourPurchaseReservationOptions
              guests={guests}
              setGuests={(g) => {
                setGuests(g);
                setSharedRoomingConsent(false);
                filterOutIrrelevantGuestsInfo(g);
              }}
              adultPrices={adultPrices}
              allowSharedRooming={allowSharedRooming}
              pricesLoading={pricesLoading}
            />
          )}
          {activeStep === 1 && (
            <TourPurchaseGuestsInfoComponent
              guests={guests}
              tourEndDate={new Date(data?.arrivalDate || new Date())}
              onIndividualSubmit={handleIndividualSubmit}
              guestsInfo={guestsInfo}
            />
          )}
          {activeStep === 2 && (
            <TourPurchaseContactInfo
              sharedContactInfo={sharedContactInfo}
              setSharedContactInfo={handleSharedContactInfoSubmit}
              guestsInfo={guestsInfo}
              setGuestContactInfoChange={handleGuestContactInfoSubmit}
              errors={contactErrors}
            />
          )}
        </Grid2>
        <Grid2 md={4}>
          <Typography gutterBottom variant="h6" component="h4">
            {t('tourPurchasePage.tourDetail')}
          </Typography>
          {data && (
            <OnlineTourCard
              tourId={data.id}
              tourName={data.name}
              tourCode={data.code}
              start={{
                departureCity: data.departureDepartureCity,
                departureAirport: data.departureDeparture,
                arrivalCity: data.departureArrivalCity,
                arrivalAirport: data.departureArrival,
              }}
              end={{
                departureCity: data.arrivalDepartureCity,
                departureAirport: data.arrivalDeparture,
                arrivalCity: data.arrivalArrivalCity,
                arrivalAirport: data.arrivalArrival,
              }}
              tourStartDate={data.departureDate}
              tourFinishDate={data.arrivalDate}
              night={data.dayCount - 1}
              day={data.dayCount}
              currencyCode={data.currencyCode}
              remainingTime={data.remainingDay}
              remainingSeat={data.availableCapacity}
              coverImg={data.images[0]?.path}
              disableActions
              hideShareAndPrint
              hideCoverImage
            />
          )}
          {selection && (
            <>
              <PurchaseSummary
                totalPrice={selection.adultPrice + selection.childrenPrice}
                guests={guests}
                guestsInfo={guestsInfo}
                adultPrice={selection.adultPricePerPerson}
                childrenPrice={selection.childrenPrice}
                childrenPrices={
                  selection.childrenPrices as IOnlineTourPriceResult[]
                }
                currencyCode={selection.price.price.currencyCode}
                room={selection.price.bedType}
                sx={{ mt: 1 }}
              />
              {activeStep === 0 && isSharedRoomingActive && (
                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={
                    <Checkbox
                      checked={sharedRoomingConsent}
                      onChange={(e, v) => setSharedRoomingConsent(v)}
                    />
                  }
                  label={t('tourPurchasePage.sharedRoomingConsent')}
                />
              )}
              <Stack direction="row" justifyContent="space-between" mt={2}>
                {activeStep > 0 ? (
                  <Button
                    color="inherit"
                    onClick={handlePrev}
                    startIcon={<ChevronLeftTwoTone />}
                  >
                    {t('tourPurchasePage.back')}
                  </Button>
                ) : (
                  <div />
                )}
                <Button
                  variant="contained"
                  onClick={handleNext}
                  size="large"
                  color="secondary"
                  disabled={!checkStep()}
                >
                  {activeStep === 0 &&
                    !token &&
                    t('tourPurchasePage.loginAndContinue')}
                  {activeStep === 0 && token && t('tourPurchasePage.book')}
                  {activeStep === 1 && t('tourPurchasePage.continue')}
                  {activeStep === 2 && t('tourPurchasePage.addToCart')}
                </Button>
              </Stack>
            </>
          )}
        </Grid2>
      </Grid2>
    </Box>
  );
}
